import { DependencyList, useEffect } from 'react';
import isEqual from 'lodash/isEqual';

import { IError } from 'src/api/types';

import { usePrevious } from './usePrevious';

/**
 * This hook allows to trigger async methods inside a useEffect
 * @param asyncFn
 * @param onSuccess
 * @param onFailure
 * @param dependencies
 */
export const useAsyncEffect = <T>(
  asyncFn: () => Promise<T> | Promise<void>,
  onSuccess?: (data: T) => void | Promise<void>,
  onFailure?: (e: IError) => void,
  dependencies: DependencyList = [],
): void => {
  const previousDependencies = usePrevious<DependencyList>(dependencies);

  useEffect(() => {
    let isMounted = true;

    const executeAsyncFunction = async () => {
      try {
        const response = await asyncFn();

        if (!response) {
          return;
        }

        if (!onSuccess || (!isMounted && isEqual(previousDependencies, dependencies))) {
          return;
        }

        await onSuccess(response);
      } catch (e) {
        if (!onFailure) {
          console.error('Error:', e);
          return;
        }

        onFailure(e as IError);
      }
    };

    executeAsyncFunction();
    return () => {
      isMounted = false;
    };
  }, [...dependencies]);
};
